import React, { useRef, useState, useEffect } from "react";
import Header from "./header";
import Footer from "./Footer";

// --- 1. Custom Hook for Intersection Observer ---
const useObserver = (options) => {
  const [isIntersecting, setIntersecting] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIntersecting(true);
        observer.unobserve(entry.target);
      }
    }, options);

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  return [elementRef, isIntersecting];
};

const HomePage = () => {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      const el = document.getElementById(id);

      if (el) {
        // Give browser a moment to render UI
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 50);
      }
    }
  }, []);
  const contactUsRef = useRef(null);

  const animationContainerStyle = {
    transition: "transform 1s ease-out, opacity 1s ease-out",
  };

  const getRevealStyle = (isIntersecting) => ({
    transform: isIntersecting ? "translateY(0)" : "translateY(50px)",
    opacity: isIntersecting ? 1 : 0,
  });

  const handleContactScroll = () => {
    contactUsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const [heroRef, heroIntersecting] = useObserver({ threshold: 0.1 });
  const [aboutUsRef, aboutUsIntersecting] = useObserver({ threshold: 0.1 });
  const [ourWorkRef, ourWorkIntersecting] = useObserver({ threshold: 0.1 });
  const [careersRef, careersIntersecting] = useObserver({ threshold: 0.1 });
  const [contactRef, contactIntersecting] = useObserver({ threshold: 0.1 });
  const isMobile = window.innerWidth < 600;
  const heroContainerStyle = {
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    paddingTop: "80px",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "50px",
    padding: "60px 8%",
    boxSizing: "border-box",
    flexWrap: "wrap",
    backgroundColor: "#fff",
  };

  const contentAreaStyle = {
    flex: "1 1 400px",
    maxWidth: "100%",
  };

  const imageStyle = {
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: "10px",
  };

  // ✅ FIXED: Overlay container with proper positioning
  const imageTextOverlayContainerStyle = {
    position: "relative",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom:"3rem"
  };

  // ✅ FIXED: Overlay text styles with proper centering
  const overlayTitleStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: "clamp(3rem, 5vw, 4rem)",
    fontWeight: "800",
    color: "black",
    textAlign: "center",
    width: "90%",
    margin: 0,
    zIndex: 10,
    whiteSpace: "normal",
    lineHeight: "1.2",
  };

  const textContentStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  };

  const subtitleStyle = {
    fontSize: "1.2rem",
    fontWeight: "600",
    color: "#555",
    marginBottom: "1.5rem",
    marginTop: isMobile?"0rem":"1rem",
  };

  const textStyle = {
    fontSize: "1rem",
    color: "#777",
    lineHeight: "1.6",
    marginBottom: "2rem",
  };

  const buttonStyle = {
    padding: "12px 25px",
    fontSize: "1rem",
    fontWeight: "bold",
    color: "black",
    backgroundColor: "#a3ff85",
    border: "none",
    borderRadius: "25px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  };

  const careerButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#b8ff9e",
    width: "90%",
    marginTop: "20px",
    textDecoration: "none",
    textAlign: "center",
    display: "inline-block",
  };

  const buttonContainerStyle = {
    display: "flex",
    gap: "20px",
    justifyContent: "center",
    flexWrap: "wrap",
  };

  const aboutUsContainerStyle = {
    ...heroContainerStyle,
    minHeight: "auto",
    backgroundColor: "#f6d7ff",
    padding: "80px 8%",
  };

  const aboutUsTextStyle = {
    fontSize: "1.1rem",
    lineHeight: "1.7",
    color: "#444",
    textAlign: "center",
    marginBottom: "20px",
  };

  const ourWorkSectionStyle = {
    ...heroContainerStyle,
    minHeight: "auto",
    backgroundColor: "#fff",
    padding: "80px 8%",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "40px",
  };

  const ourWorkHeaderContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    flexWrap: "wrap",
    gap: "20px",
  };

  const ourWorkTitleStyle = {
    fontSize: "3.5rem",
    position: "absolute",
    top: "50%",
    left: "50%",
    fontWeight: "800",
    color: "#333",
    flex: "1 1 200px",
    margin: 0,
    lineHeight: 1.1,
    textAlign: "left",
    transform: "translate(-50%, -50%)",
    whiteSpace: "nowrap",
    zIndex: 10,
  };

  const ourWorkIntroTextStyle = {
    fontSize: "1.1rem",
    lineHeight: "1.7",
    color: "#444",
    maxWidth: "600px",
    flex: "2 1 400px",
    margin: 0,
    paddingTop: "5px",
  };

  const cardImageStyle = {
    width: "100%",
    maxHeight: "180px",
    objectFit: "contain",
    marginBottom: "15px",
  };

  const cardTextStyle = {
    fontSize: "0.95rem",
    lineHeight: "1.6",
    color: "#555",
  };

  const cardsContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "30px",
    width: "100%",
  };

  const cardStyle = {
    flex: "1 1 300px",
    maxWidth: "100%",
    padding: "20px",
    borderRadius: "15px",
    border: "1px solid #ddd",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    minHeight: "450px",
  };

  const cardTitleStyle = {
    fontSize: "1.5rem",
    fontWeight: "800",
    color: "#333",
    marginBottom: "10px",
    lineHeight: "1.2",
    height: "auto",
  };

  const cardSubHeaderStyle = {
    fontSize: "0.9rem",
    fontWeight: "600",
    color: "#444",
    marginBottom: "5px",
    marginTop: "15px",
  };

  const cardListStyle = {
    fontSize: "0.95rem",
    color: "#555",
    listStyleType: "none",
    paddingLeft: "0",
    margin: "0 0 15px 0",
  };

  const listItemStyle = {
    marginBottom: "8px",
    lineHeight: "1.5",
    paddingLeft: "15px",
    position: "relative",
  };

  const customBulletStyle = {
    content: "'\\2022'",
    color: "#0c0c0bff",
    fontWeight: "bold",
    display: "inline-block",
    width: "1em",
    position: "absolute",
    left: 0,
  };

  const careersSectionStyle = {
    ...ourWorkSectionStyle,
    backgroundColor: "#f9f9f9",
    padding: "80px 8% 100px 8%",
    alignItems: "flex-start",
  };

  const careersHeaderContainerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    marginBottom: "30px",
    gap: "20px",
  };

  const careersOverlaidBoxStyle = {
    position: "relative",
    width: "100%",
    maxWidth: "400px",
  };

  const careersTitleImageStyle = {
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: "10px",
  };

  const careersTitleOverlayStyle = {
    position: "absolute",
    top: "50%",
    left: "40%",
    transform: "translate(-50%, -50%)",
    fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
    fontWeight: "900",
    color: "#333",
    lineHeight: 1,
    whiteSpace: "nowrap",
    zIndex: 10,
  };

  const careersSvgStyle = {
    height: "70px",
    width: "auto",
    position: "absolute",
    top: "55%",
    left: isMobile ? "80%" : "85%",
    transform: "translate(-50%, -50%)",
  };

  const contactUsSectionStyle = {
    ...heroContainerStyle,
    minHeight: "50vh",
    backgroundColor: "#f6d7ff",
    padding: "80px 8%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "50px",
  };

  const contactTextContainerStyle = {
    flex: "1 1 400px",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  };

  const contactTitleIconContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '15px',
    border: '2px solid #e1bfff',
    marginBottom: '20px',
  };

  const contactTitleStyle = {
    fontSize: "4rem",
    fontWeight: "900",
    color: "#333",
    marginBottom: "10px",
    lineHeight: 1,
  };

  const contactSubtitleStyle = {
    fontSize: "1.4rem",
    color: "#555",
    marginBottom: "20px",
    lineHeight: "1.3",
  };

  const socialIconStyle = {
    width: '40px',
    height: '40px',
    marginRight: '15px',
    cursor: 'pointer',
  };

  const contactDetailBoxStyle = {
    backgroundColor: "#fff",
    padding: "15px 25px",
    borderRadius: "10px",
    border: "2px solid #e1bfff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  };

  const contactLabelStyle = {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#333",
  };

  const contactValueStyle = {
    fontSize: "1.2rem",
    fontWeight: "700",
    color: "#444",
  };

  const accessibilityTextStyle = {
    fontSize: "1.5rem",
    color: "#0c0c0cff",
    marginTop: "20px",
    textAlign: "center"
  };

  const aboutUsContent = (
    <>
      <p style={aboutUsTextStyle}>
        <b>Indycium</b> delivers <b>unified marketing intelligence</b> by seamlessly connecting
        online and offline media insights for India's leading advertisers.
      </p>
      <p style={aboutUsTextStyle}>
        We build and manage advanced intelligence platforms that power competitive analysis, audience insights, and marketing performance tracking.
      </p>
      <p style={aboutUsTextStyle}>
        Designed for <b>scalability</b> and <b>continuous innovation</b>, Indycium is actively
        shaping the future of advertising-tech solutions to drive strategic growth for brands across India.
      </p>
    </>
  );
  const workCards = [
    {
      id: 1,
      imageSrc: "/images/3s.webp",
      title: "1. End-to-End Operational & Client Support",
      text: "We manage and monitor complete operational workflows, ensuring smooth and error-free service delivery. Our team provides continuous support, resolves client queries, maintains platform stability, and ensures high-performance uptime.",
    },
    {
      id: 2,
      imageSrc: "/images/3s2.jpg",
      title: "2. Managing Large-Scale Ads Archive",
      text: "We oversee one of India's largest advertising archives, covering millions of ads across press, TV, radio, digital, and outdoor media. Our work includes data validation, structuring, quality checks, indexing, and maintaining accessible historical data for research and analysis.",
    },
    {
      id: 3,
      imageSrc: "/images/3s3.webp",
      title: "3. Continuous Platform Enhancement",
      text: "We continuously improve platform features, optimize workflows, and ensure reliable delivery across all advertising formats.",
    },
  ];

  const careerCards = [
    {
      id: 1,
      title: "Python/Django & React Engineering Intern",
      header: "About the internship",
      listItems: [
        "Re-architect our Python codebase",
        "Optimize queries and code",
        "Work on becoming skilled as a senior software architect",
        "Work directly under senior developers and get trained on production level code",
        "Interact with production servers",
      ],
      link: "https://forms.gle/GxnjQ7msCxjecr4A6",
    },
    {
      id: 2,
      title: "Django & React Developer",
      header: "About the Role (Full-Time)",
      listItems: [
        "Optimize code, queries, and overall system performance",
        "Learn advanced architecture skills to reach senior-developer level",
        "Collaborate directly with senior developers on production-grade projects",
        "Work with live production servers and real-world systems",
      ],
      link: "https://forms.gle/GxnjQ7msCxjecr4A6",
    },
    {
      id: 3,
      title: "Human Resources (HR)-Internship",
      header: "The selected intern's day-to-day responsibilities include:",
      listItems: [
        "Learn the ropes of talent acquisition and recruitment",
        "Learn to use software and tools to manage employees",
        "Find good resumes and try to hire those candidates",
      ],
      link: "https://forms.gle/mgzsaXtp8gBHcqKp9",
    },
  ];

  return (
    <>
      <Header />

      {/* HERO SECTION */}
      <div style={heroContainerStyle} ref={heroRef}>
        <div style={{ ...contentAreaStyle, ...animationContainerStyle, ...getRevealStyle(heroIntersecting), transitionDelay: "0s" }}>
          <img src="/images/1s.svg" alt="Hero Visual" style={imageStyle} />
        </div>

        <div style={{ ...contentAreaStyle, ...textContentStyle, ...animationContainerStyle, ...getRevealStyle(heroIntersecting), transitionDelay: "0.2s" }}>
          <div style={imageTextOverlayContainerStyle}>
            <img src="/images/1s.png" alt="Hero Visual" style={imageStyle} />
            <h1 style={overlayTitleStyle}>Welcome to Indycium</h1>
          </div>

          <h2 style={subtitleStyle}>We Build the Engine Behind Modern Advertising Insight</h2>
          <p style={textStyle}>
            Powering advanced intelligence systems for the advertising ecosystem.
          </p>

          <div style={buttonContainerStyle}>
            <button style={buttonStyle} >
              Explore Platform
            </button>
            <button id="contactBtn" style={buttonStyle} onClick={handleContactScroll}>
              Contact Us
            </button>
          </div>
        </div>
      </div>

      {/* ABOUT US SECTION */}
      <div id="about" style={aboutUsContainerStyle} ref={aboutUsRef}>
        {/* ✅ FIXED: Left side with centered content */}
        <div style={{
          ...contentAreaStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          ...animationContainerStyle,
          ...getRevealStyle(aboutUsIntersecting),
          transitionDelay: "0s"
        }}>
          <div style={imageTextOverlayContainerStyle}>
            <img src="/images/2s.png" alt="About Us Visual" style={{ ...imageStyle, width: isMobile ? "20rem" : "20rem", margin: "0 auto", display: "block" }} />
            {/* ✅ Two separate overlays for better control */}
            <h1 style={{ ...overlayTitleStyle, fontSize: "2rem", top: isMobile ? "10%" : "3%" }}>About</h1>
            <h1 style={{ ...overlayTitleStyle, top: "50%" ,fontSize:isMobile? "3.5rem": "clamp(3rem, 5vw, 4rem)"}}>Indycium</h1>
          </div>
          {aboutUsContent}
        </div>

        {/* ✅ FIXED: Right side with centered image */}
        <div style={{
          ...contentAreaStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...animationContainerStyle,
          ...getRevealStyle(aboutUsIntersecting),
          transitionDelay: "0.2s"
        }}>
          <img src="/images/new image.png" alt="About Us Illustration" style={{ ...imageStyle, width: "80%", maxWidth: "100%" }} />
        </div>
      </div>

      {/* OUR WORK SECTION */}
      <div style={ourWorkSectionStyle} ref={ourWorkRef}>
        <div style={{ ...ourWorkHeaderContainerStyle, ...animationContainerStyle, ...getRevealStyle(ourWorkIntersecting), transitionDelay: "0s" }}>
          {/* <h2 style={ourWorkTitleStyle}>Our Work</h2> */}
          <div style={careersOverlaidBoxStyle}>
            <img src="/images/5s.png" alt="Careers Title Background" style={careersTitleImageStyle} />
            <h2 style={ourWorkTitleStyle}>Our Work</h2>
            {/* <img src="/images/career.svg" alt="Careers Graduation Cap Graphic" style={careersSvgStyle} /> */}
          </div>
          <p style={ourWorkIntroTextStyle}>
            We have been delivering daily competitive marketing insights to some of the largest marketing teams in India for over two decades.
            Our clients include top brands advertising across TV, print, radio, and digital platforms.
          </p>
        </div>
        <div style={cardsContainerStyle}>
          {workCards.map((card, index) => (
            <div key={card.id} style={{ ...cardStyle, ...animationContainerStyle, ...getRevealStyle(ourWorkIntersecting), transitionDelay: `${0.1 * index + 0.2}s` }}>
              <img src={card.imageSrc} alt={`Illustration for ${card.title}`} style={{ ...cardImageStyle, minHeight: '180px' }} />
              <h3 style={cardTitleStyle}>
                <b style={{ color: "#a3ff85" }}>|</b> {card.title.split(":").map((part, idx) =>
                  idx === 0 ? <span key={idx}>{part}:</span> : <b key={idx}>{part}</b>
                )}
              </h3>
              <p style={cardTextStyle}>{card.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CAREERS SECTION */}
      <div id="careers" style={careersSectionStyle} ref={careersRef}>
        <div style={{ ...careersHeaderContainerStyle, ...animationContainerStyle, ...getRevealStyle(careersIntersecting), transitionDelay: "0s" }}>
          <div style={careersOverlaidBoxStyle}>
            <img src="/images/5s.png" alt="Careers Title Background" style={careersTitleImageStyle} />
            <h2 style={careersTitleOverlayStyle}>Careers</h2>
            <img src="/images/career.svg" alt="Careers Graduation Cap Graphic" style={careersSvgStyle} />
          </div>
        </div>

        <div style={cardsContainerStyle}>
          {careerCards.map((job, index) => (
            <div key={job.id} style={{ ...cardStyle, ...animationContainerStyle, justifyContent: "space-between", ...getRevealStyle(careersIntersecting), transitionDelay: `${0.1 * index + 0.2}s` }}>
              <div>
                <h3 style={{ ...cardTitleStyle, minHeight: "3rem" }}>{job.title}</h3>
                <h4 style={cardSubHeaderStyle}>{job.header}</h4>
                <ul style={cardListStyle}>
                  {job.listItems.map((item, idx) => (
                    <li key={idx} style={listItemStyle}>
                      <span style={customBulletStyle}>-</span>{item}
                    </li>
                  ))}
                </ul>
              </div>

              <a href={job.link} target="_blank" rel="noopener noreferrer" style={careerButtonStyle}>
                Apply Now
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* CONTACT US SECTION */}
      <div id="contact"
        style={contactUsSectionStyle}
        ref={(el) => {
          contactUsRef.current = el;
          contactRef.current = el;
        }}
      >
        <div style={{ ...contactTextContainerStyle, ...animationContainerStyle, ...getRevealStyle(contactIntersecting), transitionDelay: "0s" }}>
          <div style={contactTitleIconContainerStyle}>
            <h2 style={contactTitleStyle}>Contact Us</h2>

            <p style={contactSubtitleStyle}>
              Schedule a free consultation or apply as a <b style={{ backgroundColor: '#a3ff85', padding: '2px 5px', borderRadius: '5px' }}>digital accessibility expert</b>
            </p>

            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <a href="https://in.linkedin.com/company/indycium" target="_blank" rel="noopener noreferrer">
                <img src="/images/link.png" alt="LinkedIn" style={{ ...socialIconStyle, height: "30px", width: "100%" }} />
              </a>

              <a href="mailto:info@indycium.com">
                <img src="/images/email.png" alt="Email" style={socialIconStyle} />
              </a>
            </div>
          </div>

          <div style={contactDetailBoxStyle}>
            <span style={contactLabelStyle}>Phone</span>
            <span style={contactValueStyle}>+91-8800XXXX85</span>
          </div>

          <div style={contactDetailBoxStyle}>
            <span style={contactLabelStyle}>Email</span>
            <span style={contactValueStyle}>info@indycium.com</span>
          </div>
        </div>

        <div style={{ ...contentAreaStyle, ...animationContainerStyle, ...getRevealStyle(contactIntersecting), transitionDelay: "0.2s" }}>
          <img src="/images/6s.svg" alt="Illustration of a person sending an email on a laptop" style={imageStyle} />
          <p style={accessibilityTextStyle}>
            <i>We are an accessible workplace.</i>
          </p>
          
        </div>
      </div>
      {/* <p style={{fontSize:"0.9rem",textAlign:"center"}}>© 2025 Indycium : All rights reserved</p> */}
      {/* FOOTER */}
      <Footer />
    </>
  );
};

export default HomePage;